import styles from "./EventHeader.module.scss";
import { HeaderEventsOptionsType } from "@/types/ModuleOptionsType";
import { Breadcrumbs } from "../../Breadcrumbs/Breadcrumbs";
import { ParentsType } from "../../../wp/getParentPages";
import { convert12To24Hour } from "../../../utils/time";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";
import { ImageType, LinkType } from "@/types/baseTypes";
import HeaderBase from "@/components/_headers/HeaderBase/HeaderBase";
import { bulmaColumnsToWidth, columnSize } from "@/utils/layout/columnSize";
import Split from "@/components/_primitives/Split/Split";
import { passACFLink } from "@/utils/passACFLink";
import { Content } from "@/components/Content/Content";
import { getCategories } from "@/wp/getCategory";
import { useState, useEffect } from "react";
import { Post } from "@aleksundshantu/schirdewan-standard-types/client";
import { getVideoData } from "@/getData/getVideoData";
import { useRouter } from "next/router";
import { HeaderVideoPlayer } from "../HeaderVideoPlayer/HeaderVideoPlayer";
import { MediaItem } from "@/components/MediaItem/MediaItem";
import { CustomLink } from "@/components/CustomLink";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import { Headline } from "@/components/Headline/Headline";
import { ContentTag } from "@/components/ContentTag/ContentTag";

export interface EventHeaderProps {
  locale: string;
  post: Post<"events">;
  headerOptions?: HeaderEventsOptionsType;
  parents: ParentsType;
  fallbackImg?: ImageType | false;
}

export const EventHeader = ({
  locale,
  post,
  headerOptions,
  parents,
  fallbackImg,
}: EventHeaderProps) => {
  const header = post.acf.header_events;
  const { getIcon } = useGlobalContext();

  const {
    overline = "",
    title,
    subline = "",
    section_margin_class,
    // background_color_class,
    start_date,
    end_date,
    media,
    location,
    headline_text_align_class,
    content,
    seo_settings: { breadcrumb_title } = {},
  } = header;

  const {
    textheader,
    imageheader,
    splitheader,
    show_fallback_image,
    breadcrumb_position,
  } = headerOptions ?? {};

  const { text_width: textheaderTextWidth } = textheader ?? {};

  const {
    text_horizontal_align: imageheaderTextHorizontalAlign,
    text_vertical_align: imageheaderTextVerticalAlign,
    vertical_align: imageheaderVerticalAlign,
    headline_gradient: imageheaderHeadlineGradient,
    text_width: imageheaderTextWidth,
    image_height: imageheaderImageHeight,
  } = imageheader ?? {};

  const {
    image_height: splitheaderImageHeight,
    media_format: splitheaderMediaFormat,
    header_width: splitheaderWidth,
    text_vertical_align: splitheaderTextVerticalAlign,
    text_width_with_spacer: splitheaderTextWidthWithSpacer,
  } = splitheader ?? {};

  const start_time = header.start_time
    ? convert12To24Hour(header.start_time)
    : undefined;
  const end_time = header.end_time
    ? convert12To24Hour(header.end_time)
    : undefined;

  const media_position = "above-from-headline";

  const show_image_header = false;
  const {
    show_overline_subline_headers: show_overline_subline,
    //media_format: mediaFormat,
  } = headerOptions ?? {};

  /* jscpd:ignore-start */
  const {
    showOverline,
    showSubline,
    showDate,
    showCategories,
    showDateAndCategories,
  } = useSublinesVisibility(show_overline_subline ?? "none");
  /* jscpd:ignore-end */
  const datePost = new Date(post.date_gmt).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const [postCategories, setPostCategories] = useState<string[]>([]);
  useEffect(() => {
    async function fetchCat() {
      const categories = await getCategories({ category: "categories" });
      setPostCategories(
        categories
          .filter((cat) => post?.categories?.includes(cat.id))
          .map((cat) => cat.name),
      );
    }
    fetchCat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const overlineJSX = `${showDate || showDateAndCategories ? datePost : ""}${showDateAndCategories ? " | " : ""}${(showCategories || showDateAndCategories) && postCategories.length > 0 ? `${postCategories.join(" | ")}` : ""}`;

  const splitHeaderDirection: "row" | "row-reverse" = "row";

  const noBox = show_image_header && media_position !== "above-from-headline";

  const [videoData, setVideoData] = useState<Post<"videos"> | null>(null);
  const router = useRouter();
  useEffect(() => {
    if (!media || media.type !== "video") return;
    const videoId = "video" in media ? media.video?.[0] : undefined;
    async function fetchVideoData() {
      const videoResponse = await getVideoData({
        videoId: videoId as string,
        fields: "acf.header_videos,link",
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });
      if (videoResponse) {
        setVideoData(videoResponse);
      }
    }
    fetchVideoData();

    //eslint-disable-next-line
  }, []);

  const contentJSX = (
    <>
      {title && (
        <>
          <Content
            headline_html_tag={"h1-posts"}
            headline={title}
            overline={
              showOverline
                ? overline
                : showDate || showCategories || showDateAndCategories
                  ? overlineJSX
                  : ""
            }
            subline={showSubline ? subline : ""}
            headline_text_align_class={headline_text_align_class}
            text_align_class={headline_text_align_class}
            text={content}
            link={false}
            isHeader={true}
          />
        </>
      )}
    </>
  );

  const infoBoxJSX = (
    <InfoBox
      locale={locale}
      start_date={start_date}
      end_date={end_date}
      start_time={start_time}
      end_time={end_time}
      location={location}
      noBox={noBox}
      styles={styles}
      getIcon={getIcon}
    />
  );

  const mediaJSX =
    media && media.type === "image" ? (
      <MediaItem
        image_desktop={
          media.image_desktop ||
          (show_fallback_image && fallbackImg ? fallbackImg : false)
        }
        image_tablet={media.image_tablet}
        image_mobile={media.image_mobile}
        image_fullhd={media.image_fullhd}
        // mediaFormat={imageheaderMediaFormat}
        sizes="(max-width: 767px) 50vw, 100vw"
        priority={true}
        mediaFormat={
          header.type === "splitheader" ? splitheaderMediaFormat : undefined
        }
        className={`${header.type === "splitheader" && splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
      />
    ) : media &&
      media.type === "video" &&
      videoData &&
      videoData.acf.header_videos.video_type === "upload" ? (
      <HeaderVideoPlayer
        video={videoData.acf.header_videos}
        autoplay={media.autoplay}
        singlePageUrl={videoData.link}
      />
    ) : (
      <></>
    );

  const breadcrumbsJSX = (
    <div className="nav-oriented-breadcrumbs">
      <Breadcrumbs
        parents={parents}
        // post={post}
        breadcrumbPositon={
          breadcrumb_position
            ? breadcrumb_position
            : "is-justify-content-flex-start"
        }
        breadcrumbTitle={breadcrumb_title}
      />
    </div>
  );

  switch (header.type) {
    case "textheader":
      return (
        <>
          <HeaderBase
            className={`${section_margin_class}`}
            header={
              <div
                className={`${styles["title-section"]} text-header has-dunkelrot-bg`}
              >
                {breadcrumbsJSX}
                <div className="section py-0">
                  <div className="container">
                    <div className="columns is-centered is-multiline">
                      <div
                        className={`column ${columnSize(textheaderTextWidth ?? false)}`}
                      >
                        <Headline
                          headline={header.title}
                          headline_html_tag="h1-posts"
                          extraClass={`is-flex is-flex-direction-column has-text-centered`}
                          // subline={overlineJSX}
                          disableAnimation
                        />
                      </div>
                      <div
                        className={`column ${columnSize(textheaderTextWidth ?? false)} is-relative`}
                      >
                        {infoBoxJSX}
                        <div className={styles.overlay}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
            content={
              <>
                {/* <div className={`${styles["info-box-section"]} py-0`}>
                  <div className="container is-flex is-justify-content-center">
                    {infoBoxJSX}
                  </div>
                </div> */}
                {content ? (
                  <ContentTag extraClass="intro-text" content={content} />
                ) : (
                  <></>
                )}
              </>
            }
            headerFrame={"frameless"}
            contentFrame={"narrow-frame"}
            narrowFrameColumns={
              columnSize(textheaderTextWidth ?? false) || "is-12"
            }
            contentHorizontalAlignment="center"
            arrangement="content-bottom"
            height={"content-height"}
          />
        </>
      );
    case "imageheader":
      return (
        <HeaderBase
          className={`${section_margin_class}`}
          header={mediaJSX}
          content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          narrowFrameColumns={
            columnSize(imageheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment={imageheaderTextHorizontalAlign || "start"}
          contentVerticalAlignment={imageheaderTextVerticalAlign || "start"}
          height={
            imageheaderImageHeight === "screen-height"
              ? "screen-height"
              : "content-height"
          }
          heightDependency="content"
          arrangement={
            imageheaderVerticalAlign === "overlay"
              ? "overlay"
              : imageheaderVerticalAlign === "media-top-text-bottom"
                ? "content-bottom"
                : "content-top"
          }
          gradient={imageheaderHeadlineGradient ?? false}
        />
      );
    case "splitheader":
      return (
        <HeaderBase
          className={`${section_margin_class}`}
          contentFrame={
            splitheaderImageHeight === "screen-height-frameless"
              ? "frameless"
              : "narrow-frame"
          }
          headerFrame={"frameless"}
          contentHorizontalAlignment="center"
          height={
            splitheaderImageHeight === "screen-height-frameless"
              ? "screen-height"
              : "content-height"
          }
          narrowFrameColumns={
            splitheaderImageHeight === "screen-height-frameless"
              ? "is-12"
              : columnSize(splitheaderWidth ?? false) || "is-12"
          }
          arrangement="content-bottom"
          header={
            splitheaderImageHeight === "screen-height-frameless"
              ? undefined
              : breadcrumbsJSX
          }
          content={
            <Split
              first={mediaJSX}
              firstClassName={"min-height-acceptor-desktop"}
              second={contentJSX}
              direction={splitHeaderDirection}
              firstVerticalAlignment={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "stretch"
                  : "start"
              }
              secondVerticalAlignment={splitheaderTextVerticalAlign}
              secondOuterFractional={
                splitheaderImageHeight === "screen-height-frameless"
                  ? breadcrumbsJSX
                  : undefined
              }
              secondClassName="section-padding-y"
              verticalBreapoints={"tablet"}
              sticky="first"
              stickyTop={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "var(--navbar-height)"
                  : "calc(var(--navbar-height) + var(--section-padding-y))"
              }
              stickyMaxHeight="calc(100dvh - var(--navbar-height))"
              gap={"8.3333%"}
              gapOn="second"
              firstWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: `calc(100% - ${bulmaColumnsToWidth(curr.number_of_cols)})`,
                      };
                    }, {})
              }
              secondWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: bulmaColumnsToWidth(
                          curr.number_of_cols,
                        ),
                      };
                    }, {})
              }
              containerWidthLimit={{
                first: false,
                second: splitheaderImageHeight === "screen-height-frameless",
              }}
            />
          }
        />
      );
  }
};

const InfoBox = ({
  locale,
  start_date,
  end_date,
  start_time,
  end_time,
  location,
  noBox,
  styles,
  getIcon,
}: {
  locale: string;
  start_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  location: Post<"events">["acf"]["header_events"]["location"];
  noBox: boolean | undefined;
  styles: Record<string, string>;
  getIcon: (name: string) => string;
}) => {
  if (!start_date && !start_time && !end_time && !location) return null;

  return (
    <div
      className={`date-box ${styles["date-box"]} ${
        noBox ? styles["no-box"] : styles["with-box"]
      }`}
    >
      {start_date && (
        <div className={`${styles["time"]} date`}>
          <span className={styles["icon-box"]}>
            <CustomSVG
              width={18}
              height={18}
              src={getIcon("date")}
              wrapper="span"
              alt="date"
              className="is-flex"
            />
          </span>

          <time>
            {new Date(start_date).toLocaleDateString(locale, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}
            {end_date &&
              ` - ${new Date(end_date).toLocaleDateString(locale, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })}`}
          </time>
        </div>
      )}

      {(start_time || end_time) && (
        <div className={`${styles["time"]} date`}>
          <span className={styles["icon-box"]}>
            <CustomSVG
              width={18}
              height={18}
              src={getIcon("time")}
              wrapper="span"
              alt="time"
              className="is-flex"
            />
          </span>

          <time className={styles["start-end-time"]}>
            {start_time}
            {end_time && ` - ${end_time}`}
          </time>
        </div>
      )}

      <LocationContent location={location} styles={styles} getIcon={getIcon} />
      {/* <div className={styles.overlay}></div> */}
    </div>
  );
};

const LocationContent = ({
  styles,
  getIcon,
  location,
}: {
  styles: Record<string, string>;
  getIcon: (name: string) => string;
  link?: LinkType;
  location: Post<"events">["acf"]["header_events"]["location"];
}) => {
  const { city, link, place, street, zip } = location || {};

  const locationJSX = (
    <>
      {place && <span>{`${place}${street || zip || city ? ":" : ""}`}</span>}
      <span> </span>
      {street && <span className="no-wrap">{street},</span>}
      <span> </span>
      <span className="no-wrap">
        {zip && <span className={`${styles.zip}`}>{zip}</span>}
        <span> </span>
        {city && <span>{city}</span>}
      </span>
    </>
  );

  return (
    <div className={`location ${styles.location}`}>
      <CustomSVG
        className="is-flex"
        width={18}
        height={18}
        src={getIcon("marker")}
        wrapper="span"
        alt="marker"
      />
      {link && link?.url ? (
        <CustomLink
          {...passACFLink(location, "link")}
          className={styles.address}
        >
          <address>{locationJSX}</address>
        </CustomLink>
      ) : (
        <address className={styles.address}>{locationJSX}</address>
      )}
    </div>
  );
};
